import { useNavigate } from "react-router-dom";
import { LogOut, Settings as SettingsIcon } from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuthStore } from "@/stores/auth.store";
import { useCurrentProject } from "@/hooks/useCurrentProject";
import { useLogout } from "@/hooks/useLogout";
import { initials } from "@/lib/formatters";
import { resolveAvatarColor } from "@/lib/avatarColor";
import { cn } from "@/lib/utils";

/**
 * Top-right user menu. The trigger keeps the existing 8×8 avatar's
 * visual identity verbatim (same size, border, initials, avatar
 * color) — clicking it opens the standard SaaS account dropdown:
 * identity block, project Settings link, Log out.
 *
 * No props by design. The Header just renders <UserMenu /> in
 * place of the static avatar; everything else (user, slug,
 * navigation, auth store) is read from hooks inside.
 */
export function UserMenu() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const { project } = useCurrentProject();
  const slug = project?.slug ?? null;
  const avatar = resolveAvatarColor(user?.avatar_color, user?.id);
  // Shared with the sidebar's bottom logout icon — one source of
  // truth for toast / redirect / cleanup so the two surfaces can't
  // drift. The hook handles the in-flight guard internally.
  const handleLogout = useLogout();

  function handleOpenSettings() {
    if (!slug) return;
    // Account settings live nested under the active project route
    // (per App.tsx — /projects/:slug/account → AccountPage). The
    // page itself is per-user, not per-project; the slug just
    // anchors it under the AppLayout so the sidebar + header stay.
    navigate(`/projects/${slug}/account`);
  }

  const displayName = user?.full_name?.trim() || null;
  const email = user?.email ?? null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label="Open user menu"
        // inline-flex + items-center align the trigger's box to the
        // 32×32 Avatar exactly. Without it, the underlying <button>
        // falls back to inline-block + baseline alignment and the
        // whole avatar drifts vs the bell icon next door.
        className="inline-flex h-8 w-8 items-center justify-center rounded-full outline-none ring-offset-2 transition-shadow focus-visible:ring-2 focus-visible:ring-indigo-400"
      >
        <Avatar className="h-8 w-8 border border-slate-200">
          <AvatarFallback
            className={cn("text-xs font-semibold", avatar.bg, avatar.text)}
          >
            {initials(displayName)}
          </AvatarFallback>
        </Avatar>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={8} className="w-64">
        <DropdownMenuLabel className="font-normal">
          <div className="flex items-center gap-3 py-1">
            <Avatar
              className="h-10 w-10 border border-slate-200"
              aria-hidden="true"
            >
              <AvatarFallback
                className={cn(
                  "text-sm font-semibold",
                  avatar.bg,
                  avatar.text,
                )}
              >
                {initials(displayName)}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              {displayName && (
                <div className="truncate text-sm font-semibold text-slate-900">
                  {displayName}
                </div>
              )}
              {email && (
                <div className="truncate text-xs text-slate-500">{email}</div>
              )}
            </div>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          disabled={!slug}
          onSelect={handleOpenSettings}
          // The account page is nested under /projects/:slug, so
          // it's unreachable from the project selector (no slug).
          // Tooltip primitive isn't nestable inside DropdownMenuContent
          // in this codebase yet — native title attribute is the
          // pragmatic fallback.
          title={slug ? undefined : "Open a project first"}
        >
          <SettingsIcon className="mr-2 h-4 w-4" />
          Settings
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={handleLogout}
          className="text-rose-600 focus:bg-rose-50 focus:text-rose-700"
        >
          <LogOut className="mr-2 h-4 w-4" />
          Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
